import { FC, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Box, Button, Flex, Grid, Img, Text, keyframes, useToast } from '@chakra-ui/react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Contest, ContestParticipant } from 'models';
import routes from 'routes';

import { useDuplicateContestMutation, useLazyGetContestParticipantQuery } from 'api/contest';

import CardPlaceholder from 'components/builder/CardPlaceholder';

import { useAppDispatch, useAppSelector } from 'hooks/app';

import { setContest } from 'store/slices/contest';

import { getEventStatusString } from './utils';
import { generateCardPlaceholderData } from './utils/cardPlaceholderUtils';

const shineAnimation = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 200px 0;
  }
`;

dayjs.extend(utc);
dayjs.extend(timezone);

type ContestListProps = {
  contests: Contest[];
  onViewChange?: (contestId: number) => void;
  onContestDuplicated?: () => void;
};

const ContestList: FC<ContestListProps> = ({ contests, onViewChange, onContestDuplicated }) => {
  const user = useAppSelector((store) => store.auth.user);
  const dispatch = useAppDispatch();
  const [getContestParticipant] = useLazyGetContestParticipantQuery();
  const [duplicateContestMutation] = useDuplicateContestMutation();

  const [participations, setParticipations] = useState<Record<number, ContestParticipant | null>>({});
  const [loadingParticipations, setLoadingParticipations] = useState<Record<number, boolean>>({});

  const navigate = useNavigate();
  const toast = useToast();
  const getStatusColor = (contest: Contest, participation?: ContestParticipant | null) => {
    if (contest.status === 'locked') {
      return '#FFFF00';
    } else if (contest.status === 'completed') {
      return participation && participation.status === 'completed' ? '#75E17B' : 'rgba(255,255,255,0.6)';
    } else {
      return 'rgba(255,255,255,0.6)';
    }
  };

  const checkIsHighlightedStatus = useCallback(
    (contest: Contest, participation?: ContestParticipant | null) => {
      if (contest.status === 'active') {
        const participantStatus = participation?.status || 'cards_pending';
        return participantStatus === 'cards_pending' || participantStatus === 'completion_pending';
      }
      return false;
    },
    [participations]
  );

  const shouldShowCardPlaceholders = useCallback(
    (contest: Contest, participation?: ContestParticipant | null, contestIndex?: number) => {
      const participantStatus = participation?.status || 'cards_pending';

      if (
        contestIndex !== undefined &&
        contestIndex >= 1 &&
        participantStatus === 'cards_pending' &&
        (participation?.cards.cardsWithStatus?.length || 0) <= 0
      ) {
        return false;
      }

      if (contest.status === 'scheduled') {
        return false;
      } else if (contest.status === 'completed') {
        return participantStatus === 'completed';
      } else if (contest.status === 'active') {
        return (
          participantStatus === 'cards_pending' ||
          participantStatus === 'completion_pending' ||
          participantStatus === 'completed'
        );
      } else if (contest.status === 'locked') {
        return participantStatus === 'completed';
      }

      return false;
    },
    [participations]
  );

  const shouldShowLineupScore = useCallback(
    (contest: Contest, participation?: ContestParticipant | null) => {
      const participantStatus = participation?.status || 'cards_pending';
      return contest.status === 'completed' && participantStatus === 'completed';
    },
    [participations]
  );

  const getToastMessage = useCallback(
    (contest: Contest, participation?: ContestParticipant | null) => {
      const participantStatus = participation?.status || 'cards_pending';
      if (contest.status === 'scheduled') {
        return "It's coming up, check later";
      } else if (contest.status === 'locked' && participantStatus !== 'completed') {
        return 'Entry closed, pick another game';
      }
      return null;
    },
    [participations]
  );

  const checkUserParticipation = useCallback(
    async (contestId: number) => {
      if (!user) return;

      setLoadingParticipations((prev) => ({ ...prev, [contestId]: true }));

      try {
        const result = await getContestParticipant(contestId);

        if (result.data) {
          setParticipations((prev) => ({ ...prev, [contestId]: result.data! }));
        } else {
          setParticipations((prev) => ({ ...prev, [contestId]: null }));
        }
      } catch (error) {
        setParticipations((prev) => ({ ...prev, [contestId]: null }));
      } finally {
        setLoadingParticipations((prev) => ({ ...prev, [contestId]: false }));
      }
    },
    [user, getContestParticipant, contests]
  );

  const onDuplicateHandler = (contest: Contest) => {
    duplicateContestMutation({ contestId: contest.id }).unwrap().then(() => {
      toast({
        duration: 3000,
        isClosable: true,
        position: 'bottom',
        render: ({ onClose }) => {
          return (
            <Flex
              alignItems="center"
              justifyContent="center"
              p="5px 18px !important"
              backgroundColor="#111111"
              borderRadius="10px"
              gap="10px"
              w="min-content"
              whiteSpace="nowrap"
              margin="0 auto 10px"
              onClick={onClose}
            >
              <Box>Contest duplicated successfully</Box>
            </Flex>
          );
        },
      });
      onContestDuplicated?.();
    });
  };

  useEffect(() => {
    if (user && contests.length > 0) {
      const contestsToCheck = contests.filter((contest) => contest.status !== 'scheduled');

      const initialLoadingState: Record<number, boolean> = {};
      contestsToCheck.forEach((contest) => {
        initialLoadingState[contest.id] = true;
      });
      setLoadingParticipations(initialLoadingState);

      const checkParticipationsInParallel = async () => {
        await Promise.allSettled(contestsToCheck.map((contest) => checkUserParticipation(contest.id)));
      };

      checkParticipationsInParallel();
    }
  }, [user, contests, checkUserParticipation]);

  const getButtonProps = useCallback(
    (contest: Contest) => {
      const participation = participations[contest.id];
      const isLoading = loadingParticipations[contest.id];
      const isFakeUser = localStorage.getItem('is_fake_user') === 'true';
      const participantStatus = participation?.status || 'cards_pending';

      if (isLoading) {
        return { text: 'loading...', isDisabled: true, to: '' };
      }

      switch (contest.status) {
        case 'completed':
          return {
            text: 'results',
            isDisabled: false,
            to: `${routes.leaderboard}?date=${dayjs(contest.startsAt).format('YYYY-MM-DD')}&friends=false&league=${contest.league}`,
          };

        case 'scheduled':
          return { text: 'join', isDisabled: true, to: '' };

        case 'locked':
          if (participantStatus === 'completed') {
            return { text: 'lineup', isDisabled: false, to: routes.squad };
          } else if (participantStatus === 'completion_pending') {
            return { text: 'finish', isDisabled: true, to: '' };
          } else {
            return { text: 'join', isDisabled: true, to: '' };
          }

        case 'active':
          if (participantStatus === 'cards_pending') {
            if (participation?.status === undefined) {
              return { text: 'join', isDisabled: false, to: routes.builder };
            } else {
              return { text: 'finish', isDisabled: false, to: routes.builder };
            }
          } else if (participantStatus === 'completion_pending') {
            return { text: isFakeUser ? 'join' : 'finish', isDisabled: false, to: routes.builder };
          } else if (participantStatus === 'completed') {
            return { text: 'lineup', isDisabled: false, to: routes.squad };
          } else {
            return { text: 'join', isDisabled: false, to: routes.builder };
          }

        default:
          return { text: 'join', isDisabled: true, to: '' };
      }
    },
    [participations, loadingParticipations]
  );

  const onButtonClick = (contest: Contest, buttonProps: { text: string; isDisabled: boolean; to: string }) => {
    if (buttonProps.isDisabled) {
      const toastMessage = getToastMessage(contest, participations[contest.id]);
      if (toastMessage) {
        toast({
          duration: 3000,
          isClosable: true,
          position: 'bottom',
          render: ({ onClose }) => {
            return (
              <Flex
                alignItems="center"
                justifyContent="center"
                p="5px 18px !important"
                backgroundColor="#111111"
                borderRadius="10px"
                gap="10px"
                w="min-content"
                whiteSpace="nowrap"
                margin="0 auto 10px"
                onClick={onClose}
              >
                <Box>{toastMessage}</Box>
              </Flex>
            );
          },
        });
      }
      return;
    }

    if (buttonProps.to) {
      dispatch(setContest(contest));
      onViewChange?.(contest.id);
      navigate(buttonProps.to);
    }
  };

  const defaultCardImage = (contest: Contest) => {
    switch (contest.league) {
      case 'nba':
      case 'wnba':
        return '/home/league=basketball.png';
      case 'nfl':
        return '/home/league=football.png';
      case 'mlb':
        return '/home/league=baseball.png';
    }
  };

  return (
    <Box p="10px 15px 12px">
      <Grid templateColumns="repeat(auto-fit, minmax(280px, 1fr))" gap="16px">
        {contests.map((contest, contestIndex) => (
          <Box
            key={contest.id}
            backgroundColor="rgba(255,255,255,0.05)"
            borderRadius="12px"
            p="12px"
            position="relative"
            overflow="hidden"
            border={
              checkIsHighlightedStatus(contest, participations[contest.id])
                ? '1px solid #F83C3C'
                : '1px solid rgba(255,255,255,0.1)'
            }
            transition="all 0.3s ease"
            style={
              loadingParticipations[contest.id]
                ? {
                  transition: 'opacity 0.2s linear',
                  backgroundImage:
                    'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0) 100%)',
                  backgroundSize: '400px 100%',
                  animation: `${shineAnimation} 1.5s infinite linear`,
                }
                : undefined
            }
            animation={loadingParticipations[contest.id] ? `${shineAnimation} 1.5s infinite linear` : undefined}
          >
            <Img
              src={defaultCardImage(contest)}
              alt={contest.league}
              w="300px"
              aspectRatio="1/1"
              position="absolute"
              top="65%"
              transform="rotate(-10deg) translateY(-50%)"
              right="-25%"
              opacity="0.2"
            />
            <Flex justifyContent="space-between" alignItems="center">
              <Flex direction="column" flex="1" gap="4px">
                <Text
                  fontSize="15px"
                  lineHeight="1"
                  fontWeight="400"
                  fontFamily="Special Gothic Expanded One"
                  color="white"
                  textTransform="uppercase"
                  display="flex"
                  alignItems="center"
                  gap="5px"
                >
                  <Img src={`/home/league-${contest.league}.svg`} alt={contest.league} w="12px" h="12px" />
                  {contest.displayName || contest.league}
                </Text>
                {getEventStatusString(contest, participations[contest.id]) && (
                  <Text
                    fontSize="12px"
                    lineHeight="1.2"
                    fontWeight="600"
                    letterSpacing="-0.02em"
                    color={getStatusColor(contest, participations[contest.id])}
                  >
                    {getEventStatusString(contest, participations[contest.id])}
                  </Text>
                )}
              </Flex>
              {user?.isAdmin && contest.displayName !== 'Duplicate' && (
                <Button fontSize="12px"
                  fontWeight="400"
                  fontFamily="Special Gothic Expanded One"
                  px="12px"
                  pt="6px"
                  pb="8px"
                  borderRadius="8px"
                  marginRight="10px"
                  h="auto !important"
                  onClick={() => onDuplicateHandler(contest)}
                  _hover={{
                    backgroundColor: 'brand.600',
                    height: 'auto',
                  }}
                  _active={{
                    bg: '#B19696',
                    boxShadow: '0px -2px 0px 0px #00000080 inset, 0px 0px 20px 0px #F83C3C40',
                    pt: '6px',
                    pb: '6px',
                    mt: '3px',
                  }}
                >
                  Duplicate
                </Button>
              )}
              {(() => {
                const buttonProps = getButtonProps(contest);
                const isDisabled = buttonProps.isDisabled;

                return (
                  <Button
                    fontSize="12px"
                    fontWeight="400"
                    fontFamily="Special Gothic Expanded One"
                    px="12px"
                    pt="6px"
                    pb="8px"
                    borderRadius="8px"
                    h="auto"
                    onClick={() => {
                      onButtonClick(contest, buttonProps);
                    }}
                    disabled={isDisabled}
                    backgroundColor={isDisabled ? '#B19696' : 'brand.500'}
                    _hover={{
                      backgroundColor: isDisabled ? '#B19696' : 'brand.600',
                    }}
                    _active={
                      isDisabled
                        ? {}
                        : {
                          bg: '#B19696',
                          boxShadow: '0px -2px 0px 0px #00000080 inset, 0px 0px 20px 0px #F83C3C40',
                          pt: '6px',
                          pb: '6px',
                          mt: '3px',
                        }
                    }
                  >
                    {buttonProps.text}
                  </Button>
                );
              })()}
            </Flex>
            {shouldShowCardPlaceholders(contest, participations[contest.id], contestIndex) && (
              <Box mt="20px">
                <Flex justifyContent="flex-start" alignItems="flex-start" gap={{ base: "5px", md: "10px" }} >
                  {generateCardPlaceholderData(participations[contest.id]).map((placeholderData) => (
                    <CardPlaceholder
                      key={placeholderData.key}
                      type={placeholderData.type}
                      card={placeholderData.card}
                      booster={placeholderData.booster}
                      noName={placeholderData.noName}
                    />
                  ))}
                </Flex>
                {/* {shouldShowLineupScore(contest, participations[contest.id]) && participations[contest.id] && (
                  <Flex justifyContent="center" mt="8px">
                    <Text fontSize="14px" fontWeight="600" color="#75E17B" fontFamily="Special Gothic Expanded One">
                      {participations[contest.id]!.totalPoints} PTS
                    </Text>
                  </Flex>
                )} */}
              </Box>
            )}
          </Box>
        ))}
      </Grid>
    </Box>
  );
};

export default ContestList;
